import React from 'react';
import styled from 'styled-components';
import { connect } from 'react-redux';
import ScreenStepper from './features/screens';
import ModalRoot from './features/modals/modal-root';
import I10n from './features/i10n';
import { setDefaultApiLocale, fetchTranslationFileData, fetchSeamless, validateSeamlessSession, checkIsPublicInternal, checkEdgePartnerAvailability } from './utils/service-http';
import ProductInformationCache from './persistence/description-cache';
import { setDescriptionMap } from './persistence/description-cache/ducks';
import { initParameters } from './utils/application-parameters/ducks';
import { DEFAULT_COUNTRY_LANGUAGE } from './utils/app-locale';
import { initAppConfiguration, appConfiguration, setLocale, setTechnologies, setAvailableRanges, setGlobalStyles, setIsInternal, activateEdgeForDistributor } from './utils/app-starter';
import { defaultLocaleData } from './locales';
import { createGlobalStyle } from 'styled-components';
import { generateDistributorColors } from './utils/distributor-color-processor';
import { showNotification } from './features/notification/ducks';
import { getHeadModules, getPacdriveKits } from './features/get-modules/ducks';
import { createSession } from './features/screens/ducks';
import { updateHandleConfigurationRestore } from './features/screens/start_configuration_screen/ducks';
import { setActiveStep, beforeLogout, synchronizeMySchneiderConfigurations } from './features/configuration-management/ducks';
import { LICENSE_MODELS, stepsConfig } from './constants';
import { getMySchneiderCountries } from './features/get-my-schneider-countries/ducks';
import { getLicenceSelectorSoftwares } from './features/screens/software_selection_screen/ducks';
import LoadingSpinner from './atoms/loading';
import './App.css';

const LoadingWrapper = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  align-items: center;
  justify-content: center;
`;

const Container = styled.div`
  position: relative;
  display: block;
  direction: ltr;
  background-color: white;
  color: rgb(98,100,105);
  ${props => !props.isWrapperMode && `box-shadow: rgb(0 0 0 / 15%) 0px 1px 3px 0px;`}
`;

let GlobalStyles = createGlobalStyle``;

class App extends React.Component {

  constructor(props) {
    super(props);
    try {
      this._search = window.parent.location.search;
    } catch (error) {
      this._search = window.location.search;
    }
    this.state = {
      appIsReady: false
    };
  }

  loadLocalization = async () => {
    let localeData = {};
    try {
      localeData = await fetchTranslationFileData();
    } catch (err) {
      console.log(err);
      localeData = defaultLocaleData;
    }

    I10n.init(localeData);
  }

  checkPlcAvailability = async () => {
    return this.props.getHeadModules('PLC', {})
      .then((response) => {
        const allReferences = [];
        for (let range of response.ranges) {
          allReferences.push(...range.references);
        }
        if (allReferences.length === 0) {
          setTechnologies({ plc: false })
        } else {
          setAvailableRanges('plc', response.ranges);
        }
      }).catch(err => {
        setTechnologies({ plc: false })
      });
  }

  checkPacAvailability = async () => {
    return this.props.getHeadModules('PAC', {})
      .then((response) => {
        const allReferences = [];
        for (let range of response.ranges) {
          allReferences.push(...range.references);
        }
        if (allReferences.length === 0) {
          setTechnologies({ pac: false });
        }
        else {
          setAvailableRanges('pac', response.ranges);
        }
      }).catch(err => {
        setTechnologies({ pac: false });
      });
  }

  checkEdgeAvailability = async () => {
    return this.props.getHeadModules('EDGE', {})
      .then((response) => {
        const allReferences = [];
        for (let range of response.ranges) {
          allReferences.push(...range.references);
        }
        if (allReferences.length === 0) {
          setTechnologies({ edge: false });
        }
        else {
          setAvailableRanges('edge', response.ranges);
        }
      }).catch(err => {
        setTechnologies({ edge: false });
      });
  }

  checkSoftwareLicenceAvailability = async() => {
    return this.props.getLicenceSelectorSoftwares().then((response) => {
      const softwareGroups = response.softwareGroups;
      if (!softwareGroups || softwareGroups.length === 0) {
        setTechnologies({ software_licence: false });
      } else {
        const softwares = [];
        softwareGroups.forEach((softwareGroup) => {
          for (const software of softwareGroup.softwares) {
            softwares.push({translationKey: software.key, label: software.label, references: software.scopedReferences?.filter(ref => ref.licenseModel === LICENSE_MODELS.PERPETUAL)
              .map(ref => { return {id: ref.id}; })});
          }
        });
        setAvailableRanges('software_licence', softwares);
      }
    }).catch(err => {
      setTechnologies({ software_licence: false });
    });
  }

  setIsPublicInternal = async () => {
    checkIsPublicInternal()
      .then((response) => {
        setIsInternal(true);
      })
      .catch((error) => {
        setIsInternal(false);
      });
  }

  initApp = async () => {
    initAppConfiguration();
    this.props.initParameters();
    setDefaultApiLocale(DEFAULT_COUNTRY_LANGUAGE);

    // Check if third party cookies are enabled
    try {
      localStorage.getItem('msxApplicationParameters');
    } catch (error) {
      this.props.showNotification(I10n.get('third_party_cookies_error.notification'), 'error', () => null);
    }

    ProductInformationCache.initProductInformationCacheService(this.props.setProductInformationCache);
    const distributorColors = generateDistributorColors(appConfiguration.color?.primary, appConfiguration.color?.secondary);
    setGlobalStyles(distributorColors);
    GlobalStyles = createGlobalStyle`
      .plc {
        ${props => `--se-secondary: ${props.color.primary};
        --se-hover-secondary: ${distributorColors.hoverColor};
        --se-secondary-contrast: ${distributorColors.textColor};
        --se-hover-secondary-contrast: ${distributorColors.hoverTextColor};
        `}
      ${props => `--se-primary: ${props.color.secondary};
      --se-primary-contrast: ${distributorColors.secondaryTextColor};
      --se-hover-primary: ${distributorColors.secondaryHoverColor};
      --se-hover-primary-contrast: ${distributorColors.secondaryHoverTextColor};
      --se-success: ${props.color.secondary};
      --se-success-contrast: ${distributorColors.secondaryTextColor};
      --qds-theme-signature-color-default: ${props.color.secondary};
      --qds-theme-signature-color-contrast: ${distributorColors.secondaryTextColor};
      --qds-theme-signature-color-hover: ${distributorColors.secondaryHoverColor};
      --qds-theme-signature-color-pressed: ${distributorColors.secondaryHoverColor};
      `}
      }
      `;

    const promesses = [this.props.createSessionRequest(), this.loadLocalization(), this.checkPlcAvailability(), this.checkPacAvailability(), this.checkEdgeAvailability(), this.checkSoftwareLicenceAvailability(), this.props.getPacdriveKits(), this.setIsPublicInternal()];
    if (appConfiguration.seamlessToken && appConfiguration.seamlessEnv) {
      promesses.push(fetchSeamless(appConfiguration.seamlessToken, appConfiguration.seamlessEnv, appConfiguration.idpApp));
    } else {
      const countries = await this.props.getMySchneiderCountries() || [];
      if ([...countries].includes(appConfiguration.locale)) {
        try {
          // On public website, check if existing My Schneider session is running.
          if (window.self === window.top && appConfiguration.seamlessEnv == null) {
            const isValid = await validateSeamlessSession();
            // Redirection will be performed, do not continue the function
            if (isValid) {
              return;
            }
          }
        } catch { }
      }
    }
    if (window.top !== window.self) {
      promesses.push(checkEdgePartnerAvailability()
        .then((result) => {
          if (result?.data?.payload === true) {
            activateEdgeForDistributor();
          }
        }).catch(() => {}));
    }
    await Promise.allSettled(promesses);
    if (appConfiguration.seamlessSession) {
      synchronizeMySchneiderConfigurations();
    }

    // Seamless callback function when the locale changes.
    window.msxConfiguration.setLocale = (locale) => {
      setLocale(locale);
      ProductInformationCache.clearCache();
      this.props.getPacdriveKits();
      fetchTranslationFileData().then((localeData) => {
        I10n.init(localeData);
      }).catch(error => { console.log(error); }).finally(() => {
        // Allow to retrigger the configuration restore, go to first screen and force refresh of this component.
        this.props.updateHandleConfigurationRestore(true);
        this.props.setActiveStep(stepsConfig.start_screen);
        this.forceUpdate();
      });
    }

    window.msxConfiguration.beforeLogout = () => {
      return this.props.beforeLogout();
    }


    this.setState({ appIsReady: true });
    document.dispatchEvent(
      new CustomEvent('PLC_APP_READY', {
        bubbles: true
      })
    );
  }

  componentDidMount() {
    if (window.self !== window.top && !window.msxConfiguration) {
      window.addEventListener('message', (event) => {
        if (!event.data) {
          return;
        }
        if (event.data.type === 'ScWidgetStartupEvent') {
          console.debug('bootstrap: received ScWidgetStartupEvent, setting option in window element', event.data);
          window.msxConfiguration = event.data.options;
          window.msxConfiguration.hostname = event.data.hostname;
          this.initApp();
        }
      }, false);
    } else {
      this.initApp();
    }
  }

  // Give the locale as key, in order to rerender the start screen when the application change languages. For Seamless language change (retrigger the configuration restore modal).
  render = () => {
    return (
      this.state.appIsReady ?
      <Container isWrapperMode={window.self !== window.top} className="plc" key={appConfiguration.locale}>
        <GlobalStyles color={appConfiguration.color} />
        <ScreenStepper></ScreenStepper>
        <ModalRoot></ModalRoot>

      </Container>
      :
      <LoadingWrapper className="plc" key={appConfiguration.locale}>
        <LoadingSpinner></LoadingSpinner>
      </LoadingWrapper>
    );
  }
}

const mapDispatchToProps = dispatch => {
  return {
    setProductInformationCache: (cache) => dispatch(setDescriptionMap(cache)),
    initParameters: () => dispatch(initParameters()),
    showNotification: (text, type, buttonElement) => dispatch(showNotification(text, type, buttonElement, 10000)),
    getPacdriveKits: () => dispatch(getPacdriveKits()),
    createSessionRequest: () => dispatch(createSession()),
    updateHandleConfigurationRestore: (status) => dispatch(updateHandleConfigurationRestore(status)),
    setActiveStep: (step) => dispatch(setActiveStep(step)),
    getMySchneiderCountries: () => dispatch(getMySchneiderCountries()),
    beforeLogout: () => dispatch(beforeLogout()),
    getHeadModules: (starterType, criteria) => dispatch(getHeadModules(starterType, criteria)),
    getLicenceSelectorSoftwares: () => dispatch(getLicenceSelectorSoftwares())
  };
};

export default connect(null, mapDispatchToProps)(App);
